<!-- YoutubePlayer.vue -->
<template>
    <div class="container d-flex mb-1">
        <div class="player-wrapper">
            <div v-if="isYoutubeEnabled" :id="playerId" class="youtube-player"></div>
            <YouTubeConsentPlaceholder v-else />
        </div>
    </div>
    <div v-if="isYoutubeEnabled" class="controls mt-0">
        <div class="player-controls__zone player-controls__zone--start">
            <button @click="toggleSpeed" class="btn btn-outline-secondary btn-lg text-black" :class="{ active: isSlow }">
                <i v-if="isSlow" class="fas fa-running text-white"></i>
                <i v-else class="fas fa-running"></i>
            </button>
        </div>

        <div class="player-controls__zone player-controls__zone--center">
            <!-- Botón Retroceder 1 segundo -->
            <button class="btn btn-secondary btn-lg border border-1 border-black" @click="backwardOneSecond">
                <i class="fa-solid fa-angles-left"></i>
            </button>

            <!-- Botón de Play/Pausa -->
            <button class="btn btn-secondary btn-lg border border-1 border-black mx-1" @click="togglePlayPause">
                <i :class="isPlaying ? 'fas fa-pause' : 'fas fa-play'"></i>
            </button>

            <!-- Botón Adelantar 1 segundo -->
            <button class="btn btn-secondary btn-lg border border-1 border-black" @click="forwardOneSecond">
                <i class="fa-solid fa-angles-right"></i>
            </button>
        </div>

        <div class="player-controls__zone player-controls__zone--end">
            <button v-if="!readOnly" :disabled="isDeleteButtonDisabled" class="btn btn-danger btn-lg" @click="deleteVideoUrl()">
                <i class="fas fa-cancel"></i>
            </button>
        </div>
    </div>
    <input
        v-if="isYoutubeEnabled"
        type="range"
        class="form-range"
        :value="progress"
        @input="seekToTime"
        :min="0"
        :max="duration"
        step="0.1"
        :style="'background: linear-gradient(to right, #4CAF50 0%, #4CAF50 ' + (progress / duration * 100) + '%, #ddd ' + (progress / duration * 100) + '%, #ddd 100%);'"
    />
    <ConfirmationModal
        ref="confirmationModal"
        :message="modalMessage"
        :showCancel="showCancel"
        :showRuleSelector="false"
        :ruleOptions="[]"
        :showNameInput="false"
        :initialRule="''"
        :initialName="''"
        @confirmed="handleConfirmed"
        @canceled="cancelModal"
    />
</template>

<script>
import ConfirmationModal from "@/components/competitions/ConfirmationModal.vue";
import YouTubeConsentPlaceholder from "@/components/common/YouTubeConsentPlaceholder.vue";
import {mapState} from "vuex";
import { getYoutubeIframeApi } from '@/services/youtubeIframeApi';

export default {
    components: { ConfirmationModal, YouTubeConsentPlaceholder },

    emits: ['delete-video-url', 'refresh-box-size'],

    props: {
        videoUrl: {
            type: String,
            required: true,
        },
        canDelete: {
            type: Boolean,
            default: true,
        },
        isJudgeTrainingRound: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            round_view : this.roundCompetitionIdToView || this.trainingRoundsIdToView,
            playerId: `player-${Math.random().toString(36).substr(2, 9)}`,
            player: null,
            duration: 0,
            clock: 0,
            availableSpeeds: [0.25, 0.5, 1, 1.5, 2],
            selectedSpeed: 1,
            isPlaying: false,
            isSlow: false,
            progress: 0,
            markers: [],
            pendingConfirmResolver: null,
            showCancel: false,
            modalMessage: '',
        };
    },
  computed: {
    ...mapState('user', ['isAuthenticated','user','token']),
    ...mapState("competition",["competitionIdToView", "competitionData","appNavigation"]),
    ...mapState("round",["competitionRounds","competitionPodiums","roundCompetitionIdToEdit","roundCompetitionIdToView","roundCompetitionIdToCreate"]),
    ...mapState('training', ['trainingRounds','trainingRoundsIdToView','trainingRoundsIdToCreate','trainingRoundsIdToEdit']),

    isDelete(){
      return this.roundCompetitionIdToView || this.trainingRoundsIdToView;
        },
        isDeleteButtonDisabled() {
            if (this.readOnly) {
                return true;
            }
            if (this.isJudgeTrainingRound) {
                return !this.canDelete;
            }
            return this.isDelete;
        },
        isYoutubeEnabled() {
            return this.$store.getters['cookieConsent/isYoutubeEnabled'];
        }
  },
    methods: {
        destroyPlayer() {
            if (this.player && typeof this.player.destroy === 'function') {
                this.player.destroy();
            }

            this.player = null;
            this.isPlaying = false;
            this.progress = 0;
        },
        initializePlayer() {
            if (!this.isYoutubeEnabled) {
                return Promise.resolve();
            }

            return getYoutubeIframeApi();
        },

        async onYouTubeIframeAPIReady() {
            if (!this.isYoutubeEnabled) {
                return;
            }

            await this.initializePlayer();
            const waitForYT = () => {
                if (window.YT && typeof window.YT.Player === 'function') {
                    this.createYouTubePlayer();
                } else {
                    setTimeout(waitForYT, 200);
                }
            };

            waitForYT();
        },

        createYouTubePlayer() {
            if (!this.isYoutubeEnabled) {
                return;
            }

            if (this.player) {
                this.player.destroy();
            }
            this.player = new window.YT.Player(this.playerId, {
                height: '100%',
                width: '100%',
                videoId: this.getVideoIdFromUrl(this.videoUrl),
                playerVars: {
                    'controls': 0,
                    'autoplay': 0,
                    'showinfo': 0,
                    'fs': 0,
                    'rel': 0,
                    'iv_load_policy': 3,
                    'modestbranding': 1,
                    'playsinline': 1
                },
                events: {
                    'onReady': this.onPlayerReady,
                    'onStateChange': this.onStateChange,
                },
            });

            requestAnimationFrame(() => {
                const host = this.$el?.querySelector?.(`#${this.playerId}`);
                const iframe = host?.querySelector?.('iframe');

                if (host instanceof HTMLElement) {
                    host.style.width = '100%';
                    host.style.height = '100%';
                    host.style.display = 'block';
                }

                if (iframe instanceof HTMLIFrameElement) {
                    iframe.style.width = '100%';
                    iframe.style.height = '100%';
                    iframe.style.display = 'block';
                }
            });
        },
        onPlayerReady() {
            this.player.playVideo();
            this.duration = this.player.getDuration();
            this.clock = this.player.getDuration();
            this.initializeMarkers();
            this.$emit('refresh-box-size');
        },
        onStateChange(event) {
            const playerState = event.data;
            if (playerState === 1) {
                this.isPlaying = true;
                this.player.mute()
                this.updateProgress();
            } else if (playerState === 0) {
                this.isPlaying = false;
            } else if (playerState === 2 || playerState === 5) {
                this.isPlaying = false;
            } else if (playerState === 3) {
                return;
            }
        },
        initializeMarkers() {
            const interval = 5; // Intervalo en segundos
            const numMarkers = Math.floor(this.duration / interval);
            this.markers = Array.from({ length: numMarkers }, (_, index) => index * interval);
        },
        updateProgress() {
            const update = () => {
                if (!this.player || typeof this.player.getCurrentTime !== 'function') return;
                this.progress = this.player.getCurrentTime();

                const duration = typeof this.player.getDuration === 'function'
                    ? (this.player.getDuration() || this.duration || 0)
                    : (this.duration || 0);

                if (this.isPlaying) {
                    this.clock = duration - this.player.getCurrentTime();
                    requestAnimationFrame(update);
                }
            };
            update();
        },

        formatTime(seconds) {
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = Math.floor(seconds % 60);
            const formattedMinutes = String(minutes).padStart(2, '0');
            const formattedSeconds = String(remainingSeconds).padStart(2, '0');
            return `${formattedMinutes}:${formattedSeconds}`;
        },
        toggleSpeed() {
            this.isSlow = !this.isSlow;
            const speed = this.isSlow ? 0.25 : 1.0;
            this.changeSpeed(speed);
        },
        changeSpeed(speed) {
            this.player.setPlaybackRate(speed);
        },
        seekToTime(event) {
            // Lógica para buscar a una posición específica en el tiempo del video
            const targetTime = parseFloat(event.target.value);
            if (this.player) {
                this.player.seekTo(targetTime, true);
                this.updateProgress();
                this.clock = this.duration - targetTime;
            }
        },
        async deleteVideoUrl() {
            if (!this.canDelete) {
                return;
            }
            const isConfirmed = await this.confirmWithModal('Are you sure you want to delete the video from this round?');
            if (isConfirmed){
                this.$emit('delete-video-url');
            }
        },
        handleConfirmed() {
            if (!this.pendingConfirmResolver) return;
            const resolve = this.pendingConfirmResolver;
            this.resetConfirmationModalState();
            resolve(true);
        },
        cancelModal() {
            if (!this.pendingConfirmResolver) {
                this.resetConfirmationModalState();
                return;
            }
            const resolve = this.pendingConfirmResolver;
            this.resetConfirmationModalState();
            resolve(false);
        },
        resetConfirmationModalState() {
            this.pendingConfirmResolver = null;
            this.showCancel = false;
            this.modalMessage = '';
        },
        confirmWithModal(message) {
            return new Promise((resolve) => {
                this.pendingConfirmResolver = resolve;
                this.showCancel = true;
                this.modalMessage = message;
                this.$refs.confirmationModal.openModal();
            });
        },
        getVideoIdFromUrl(url) {
            if (!url) {
                return '';
            }

            const normalized = url.trim();

            // Standard watch URLs: https://www.youtube.com/watch?v=VIDEO_ID
            const watchMatch = normalized.match(/[?&]v=([^?&#]+)/);
            if (watchMatch && watchMatch[1]) {
                return watchMatch[1];
            }

            // Short links: https://youtu.be/VIDEO_ID
            const shortMatch = normalized.match(/youtu\.be\/([^?&#/]+)/);
            if (shortMatch && shortMatch[1]) {
                return shortMatch[1];
            }

            // Embed format: https://www.youtube.com/embed/VIDEO_ID
            const embedMatch = normalized.match(/youtube\.com\/embed\/([^?&#/]+)/);
            if (embedMatch && embedMatch[1]) {
                return embedMatch[1];
            }

            // Shorts format: https://www.youtube.com/shorts/VIDEO_ID
            const shortsMatch = normalized.match(/youtube\.com\/shorts\/([^?&#/]+)/);
            if (shortsMatch && shortsMatch[1]) {
                return shortsMatch[1];
            }

            // Legacy /live or /v formats
            const liveMatch = normalized.match(/youtube\.com\/(?:live|v)\/([^?&#/]+)/);
            if (liveMatch && liveMatch[1]) {
                return liveMatch[1];
            }

            return '';
        },
        togglePlayPause() {
            if (!this.player) return;
            if (this.isPlaying) {
                this.player.pauseVideo();
            } else {
                this.player.playVideo();
            }
            this.isPlaying = !this.isPlaying;
        },
        stepVideo(deltaSeconds) {
            if (!this.player || typeof this.player.getCurrentTime !== 'function' || typeof this.player.seekTo !== 'function') return;

            const wasPlaying = this.isPlaying;
            const duration = typeof this.player.getDuration === 'function'
                ? (this.player.getDuration() || this.duration || 0)
                : (this.duration || 0);
            const currentTime = this.player.getCurrentTime() || 0;
            const maxTime = duration > 0 ? Math.max(duration - 0.05, 0) : Number.MAX_SAFE_INTEGER;
            const targetTime = Math.min(Math.max(currentTime + deltaSeconds, 0), maxTime);

            this.player.seekTo(targetTime, true);
            this.progress = targetTime;
            this.clock = Math.max((this.duration || duration || 0) - targetTime, 0);

            if (wasPlaying && typeof this.player.playVideo === 'function') {
                this.player.playVideo();
                this.isPlaying = true;
            } else if (!wasPlaying && typeof this.player.pauseVideo === 'function') {
                this.player.pauseVideo();
            }
        },
        backwardOneSecond() {
            this.stepVideo(-1);
        },
        forwardOneSecond() {
            this.stepVideo(1);
        },
    },
    watch: {
        async videoUrl(newUrl) {
            if (!this.isYoutubeEnabled) {
                this.destroyPlayer();
                return;
            }

            if (newUrl) {
                this.destroyPlayer();
                await this.initializePlayer().then(() => {
                    this.onYouTubeIframeAPIReady();
                });
            } else if (this.player) {
                this.destroyPlayer();
                await this.initializePlayer().then(() => {
                    this.onYouTubeIframeAPIReady();
                });
            }
        },
        isYoutubeEnabled(enabled) {
            if (!enabled) {
                this.destroyPlayer();
                return;
            }

            if (this.videoUrl) {
                this.$nextTick(() => {
                    this.initializePlayer().then(() => {
                        this.onYouTubeIframeAPIReady();
                    });
                });
            }
        },
    },

    mounted() {
        if (!this.isYoutubeEnabled) {
            return;
        }

        this.initializePlayer().then(() => {
            this.onYouTubeIframeAPIReady();
        });
    },

    beforeUnmount() {
        this.destroyPlayer();
    },

};
</script>

<style scoped>
.container {
    position: relative; /* Añade una posición relativa al contenedor */
    width: 100%;
    padding: 0;
    margin: 0;
}
.youtube-player {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}

.player-wrapper {
    position: relative;
    flex: 1 1 0;
    width: 100%;
    min-width: 0;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    border-radius: 12px;
    background: #000;
}

.controls {
    margin-top: 15px;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    align-items: center;
    gap: 10px;
}

.player-controls__zone {
    display: flex;
    align-items: center;
    min-width: 0;
}

.player-controls__zone--start {
    justify-content: flex-start;
}

.player-controls__zone--center {
    justify-content: center;
}

.player-controls__zone--end {
    justify-content: flex-end;
}

.controls .btn {
    flex-shrink: 0;
}

.time-counter {
    position: absolute;
    bottom: 6px;
    left: 50%;
    transform: translateX(-50%);
    font-family: 'Arial', sans-serif;
    font-size: 22px;
    text-align: center;
    color: rgba(0, 0, 0, 0.7);
    z-index: 4;
    pointer-events: none;
    padding: 4px 10px;
    border-radius: 20px;
}
/* Estilo para ocultar la apariencia predeterminada del control de rango */
input[type="range"] {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    width: 100%;
    height: 10px;
    margin: 18px 0;
    border-radius: 5px;
    background: #ddd;
    outline: none;
}
/* Establecer color del riel del control de rango antes del pulgar - Webkit */
input[type="range"]::-webkit-slider-runnable-track {
    background-color: #ddd; /* Color gris para la parte no reproducida */
}

/* Establecer color del riel del control de rango antes del pulgar - Firefox */
input[type="range"]::-moz-range-track {
    background-color: #ddd; /* Color gris para la parte no reproducida */
}

/* Establecer color del pulgar del control de rango - Webkit */
input[type="range"]::-webkit-slider-thumb {
    background-color: #4CAF50; /* Color verde para la parte reproducida */
    border-color: #4CAF50;
}

/* Establecer color del pulgar del control de rango - Firefox */
input[type="range"]::-moz-range-thumb {
    background-color: #4CAF50; /* Color verde para la parte reproducida */
    border-color: #4CAF50;
}

/* Estilo para el pulgar (bolita) cuando se arrastra - Webkit */
input[type="range"]:focus::-webkit-slider-thumb {
    box-shadow: 0 0 0 1px #ffffff, 0 0 0 0.2rem rgba(76, 175, 80, 0.25);
}

/* Estilo para el pulgar (bolita) cuando se arrastra - Firefox */
input[type="range"]:focus::-moz-range-thumb {
    box-shadow: 0 0 0 1px #ffffff, 0 0 0 0.2rem rgba(76, 175, 80, 0.25);
}

/*.active {
    background-color: #007bff;
    color: #fff;
}*/

@media (max-width: 820px) {
    .overlay-content {
        grid-template-columns: 1fr;
        gap: 12px;
        height: 56px;
        font-size: 24px;
    }

    .controls {
        gap: 6px;
        margin-top: 10px;
    }

    .controls .btn {
        padding: 0.45rem 0.6rem;
        font-size: 0.95rem;
        line-height: 1.1;
    }

    input[type="range"] {
        margin: 12px 0;
    }
}

@media (max-width: 640px) {
    .controls {
        gap: 4px;
    }

    .controls .btn {
        padding: 0.35rem 0.45rem;
        font-size: 0.82rem;
    }

    .player-controls__zone--center {
        gap: 2px;
    }
}
</style>
